// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { DEFAULT_METRIC } from "./consts.js";

/**
 * @typedef {Object} ParamDefaults
 * @property {string} metric
 * @property {string} state
 * @property {string | null} district
 * @property {string} level
 */

/** @type {ParamDefaults} */
export const PARAM_DEFAULTS = {
  metric: DEFAULT_METRIC,
  state: "00",
  district: null,
  level: "state"
};

const PARAM_KEYS = ["metric", "state", "district", "level"];

/**
 * Read URL search params and return an object with known keys.
 * Missing params fall back to defaults.
 * @param {ParamDefaults} [defaults]
 * @returns {ParamDefaults}
 */
export function readParams(defaults = PARAM_DEFAULTS) {
  const searchParams = new URLSearchParams(window.location.search);
  return {
    metric: searchParams.get("metric") ?? defaults.metric,
    state: searchParams.get("state") ?? defaults.state,
    district: searchParams.get("district") ?? defaults.district,
    level: searchParams.get("level") ?? (searchParams.has("district") ? "district" : defaults.level)
  };
}

/**
 * Write state to URL via history.replaceState.
 * Omits params that match their default value.
 * Preserves any query params not owned by this tool.
 * @param {ParamDefaults} params
 * @param {ParamDefaults} [defaults]
 */
export function writeParams(params, defaults = PARAM_DEFAULTS) {
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;

  // Remove all owned keys first
  for (const key of PARAM_KEYS) {
    searchParams.delete(key);
  }

  // Only write non-default values
  if (params.metric !== defaults.metric) {
    searchParams.set("metric", params.metric);
  }
  if (params.state !== defaults.state) {
    searchParams.set("state", params.state);
  }
  if (params.district != null) {
    searchParams.set("district", params.district);
  }
  if (params.level !== defaults.level) {
    searchParams.set("level", params.level);
  }

  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
}

/**
 * Validate param values against known metrics, geo IDs, and levels.
 * Invalid values are replaced with defaults.
 * @param {ParamDefaults} params
 * @param {Array<string>} validMetrics - list of valid metric names
 * @param {Map<string, any>} validStates - map of valid state GEOIDs (idMap)
 * @param {Map<string, Set<string>>} levelMap - map of metric to available levels
 * @param {ParamDefaults} [defaults]
 * @returns {ParamDefaults}
 */
export function validateParams(params, validMetrics, validStates, levelMap, defaults = PARAM_DEFAULTS) {
  const metric = validMetrics.includes(params.metric) ? params.metric : defaults.metric;

  const state = validStates.has(params.state) ? params.state : defaults.state;

  const validLevels = ["state", "district", "national"];
  let level = validLevels.includes(params.level) ? params.level : defaults.level;

  // If the metric doesn't support district level, downgrade to state
  if (level === "district" && !levelMap.get(metric)?.has("district")) {
    level = "state";
  }

  let district = params.district;
  if (district != null) {
    // District must be a 4-digit string and its state prefix must match the selected state
    const isValidFormat = /^\d{4}$/.test(district);
    const matchesState = district.slice(0, 2) === state;
    if (!isValidFormat || !matchesState || level !== "district") {
      district = defaults.district;
    }
  }

  return { metric, state, district, level };
}
